import React, { createContext, useContext } from 'react';
import { supabase } from '../supabase/supabase';
import { useTenant } from './TenantContext';

const CRMDataContext = createContext(null);

const notConnectedResult = () => ({
  error: 'CRMDataProvider not connected yet'
});

const isUuid = value => (
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
);

const getEntityId = entity => (
  entity?.id ||
  entity?.company_id ||
  entity?.contact_id ||
  entity?.deal_id ||
  null
);

const createError = message => ({
  success: false,
  error: { message }
});

const invalidTenantMessage = 'Mangler gyldig aktiv tenant. CRM-data ble ikke oppdatert.';

const normalizeOrgNumber = value => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits || null;
};

const normalizeEmployeesCount = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);
  return Number.isInteger(number) ? number : null;
};

const normalizeRawData = value => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : null
);

const getFirstDefined = (...values) => (
  values.find(value => value !== undefined)
);

const getCanonicalOrgNumber = input => (
  normalizeOrgNumber(getFirstDefined(
    input?.org_number,
    input?.orgnr,
    input?.organization_number,
    input?.organizationNumber,
    input?.organisasjonsnummer,
    input?.orgNumber
  ))
);

const getBrregFields = data => {
  const fields = {};
  const values = {
    org_number: getFirstDefined(
      data?.org_number,
      data?.orgnr,
      data?.organization_number,
      data?.organizationNumber,
      data?.organisasjonsnummer,
      data?.orgNumber
    ),
    address: data?.address,
    postal_code: getFirstDefined(data?.postal_code, data?.zip, data?.postalCode),
    country: data?.country,
    company_type: getFirstDefined(data?.company_type, data?.companyType, data?.orgform),
    nace_code: getFirstDefined(data?.nace_code, data?.naceCode, data?.naeringskode),
    nace_description: getFirstDefined(
      data?.nace_description,
      data?.naceDescription,
      data?.naeringskode_desc
    ),
    employees_count: getFirstDefined(
      data?.employees_count,
      data?.employeesCount,
      data?.antall_ansatte
    ),
    brreg_status: getFirstDefined(data?.brreg_status, data?.brregStatus, data?.status_brreg),
    brreg_registered_at: getFirstDefined(
      data?.brreg_registered_at,
      data?.brregRegisteredAt
    ),
    brreg_last_synced_at: getFirstDefined(
      data?.brreg_last_synced_at,
      data?.brregLastSyncedAt
    ),
    brreg_source: getFirstDefined(data?.brreg_source, data?.brregSource),
    brreg_raw_data: getFirstDefined(data?.brreg_raw_data, data?.brregRawData),
    brreg_sync_status: getFirstDefined(
      data?.brreg_sync_status,
      data?.brregSyncStatus
    ),
    brreg_sync_error: getFirstDefined(data?.brreg_sync_error, data?.brregSyncError),
    brreg_import_job_id: getFirstDefined(
      data?.brreg_import_job_id,
      data?.brregImportJobId
    )
  };

  Object.entries(values).forEach(([field, value]) => {
    if (value === undefined) {
      return;
    }

    if (field === 'org_number') {
      fields[field] = normalizeOrgNumber(value);
      return;
    }

    if (field === 'employees_count') {
      fields[field] = normalizeEmployeesCount(value);
      return;
    }

    if (field === 'brreg_raw_data') {
      fields[field] = normalizeRawData(value);
      return;
    }

    fields[field] = value ?? null;
  });

  return fields;
};

const normalizeCompany = data => {
  const companyId = data?.company_id || data?.id;

  return {
    ...data,
    id: data?.id || data?.company_id || null,
    company_id: companyId || null,
    companyId: companyId || null,
    name: data?.name || '',
    industry: data?.industry || '',
    website: data?.website || '',
    city: data?.city || '',
    status: data?.status || 'Lead',
    org_number: normalizeOrgNumber(
      data?.org_number ||
      data?.orgnr ||
      data?.organization_number ||
      data?.organizationNumber ||
      data?.organisasjonsnummer ||
      data?.orgNumber
    ),
    address: data?.address || '',
    postal_code: data?.postal_code || data?.zip || data?.postalCode || '',
    country: data?.country || '',
    ...getBrregFields(data),
    logo: data?.logo || '',
    size: data?.size || '',
    gdpr_status: data?.gdpr_status || data?.gdprStatus || '',
    tags: Array.isArray(data?.tags) ? data.tags : [],
    notes: data?.notes || '',
    parent_company_id: data?.parent_company_id || data?.parentCompanyId || null,
    owner_user_id: data?.owner_user_id || data?.ownerUserId || null,
    source: data?.source || '',
    phone: data?.phone || '',
    email: data?.email || data?.epost || '',
    linkedin_url: data?.linkedin_url || data?.linkedinUrl || '',
    description: data?.description || '',
    tenant_id: data?.tenant_id || null
  };
};

const normalizeCompanyRows = rows => (
  Array.isArray(rows) ? rows.map(normalizeCompany) : []
);

const normalizeRows = (rows, key) => (
  Array.isArray(rows)
    ? rows.map(row => ({ ...row, [`${key}_id`]: row.id }))
    : []
);

const normalizePipeline = data => {
  const tenantId = data?.tenant_id ?? data?.tenantId ?? null;
  const ownerCompanyId = data?.owner_company_id ?? data?.ownerCompanyId ?? null;
  const projectId = data?.project_id ?? data?.projectId ?? null;

  return {
    ...data,
    id: data?.id || null,
    tenant_id: tenantId,
    tenantId,
    owner_company_id: ownerCompanyId,
    ownerCompanyId,
    project_id: projectId,
    projectId,
    name: data?.name || '',
    description: data?.description || '',
    status: data?.status || 'Active',
    created_by: data?.created_by || null,
    created_at: data?.created_at || null,
    updated_at: data?.updated_at || null
  };
};

const normalizePipelineRows = rows => (
  Array.isArray(rows) ? rows.map(normalizePipeline) : []
);

const normalizeProject = data => ({
  ...data,
  id: data?.id || null,
  tenant_id: data?.tenant_id || null,
  client_company_id: data?.client_company_id || null,
  is_internal: data?.is_internal === true,
  name: data?.name || '',
  description: data?.description || '',
  project_type: data?.project_type || 'Salg',
  project_for: data?.project_for || 'Internt prosjekt',
  status: data?.status || 'Planlagt',
  start_date: data?.start_date || null,
  end_date: data?.end_date || null,
  budget: data?.budget ?? 0,
  target_value: data?.target_value ?? 0,
  target_unit: data?.target_unit || 'kroner',
  target_period: data?.target_period || 'Totalt for prosjektet',
  pricing_model: data?.pricing_model || 'Ikke avklart',
  agreed_price: data?.agreed_price ?? null,
  created_by: data?.created_by || null,
  created_at: data?.created_at || null,
  updated_at: data?.updated_at || null
});

const normalizeProjectRows = rows => (
  Array.isArray(rows) ? rows.map(normalizeProject) : []
);

const normalizeDeal = data => {
  const normalizedOwnerUserId =
    data?.owner_user_id ||
    data?.user_id ||
    data?.ownerUserId ||
    data?.assigned_user_id ||
    null;

  return {
    ...data,
    deal_id: data?.deal_id || data?.id || null,
    company_id: data?.company_id || null,
    client_company_id: data?.client_company_id || null,
    pipeline_id: data?.pipeline_id || null,
    owner_user_id: normalizedOwnerUserId,
    user_id: data?.user_id || normalizedOwnerUserId,
    ownerUserId: normalizedOwnerUserId
  };
};

const normalizeDealRows = rows => (
  Array.isArray(rows) ? rows.map(normalizeDeal) : []
);

const normalizeContactRows = rows => (
  Array.isArray(rows)
    ? rows.map(row => {
      const relationStrength = row.relation_strength
        ?? row.relationStrength
        ?? row.relationship_strength
        ?? 5;

      return {
        ...row,
        contact_id: row.id,
        title: row.title || row.job_title || row.position || row.role || '',
        phone: row.phone || row.mobile || row.phone_number || '',
        mobile: row.mobile || row.phone || row.phone_number || '',
        email: row.email || row.epost || '',
        company_id: row.company_id || row.companyId || null,
        companyId: row.company_id || row.companyId || null,
        relation_strength: relationStrength,
        relationStrength
      };
    })
    : []
);

const getActivityRow = activity => ({
  title: activity?.title?.trim() || '',
  type: activity?.type || 'Oppgave',
  status: activity?.status || 'Planlagt',
  date: activity?.date || new Date().toISOString(),
  end_date: activity?.end_date || activity?.endDate || null,
  platform: activity?.platform || '',
  meeting_link: activity?.meeting_link || '',
  contact_id: activity?.contact_id || null,
  company_id: activity?.company_id || activity?.companyId || null,
  deal_id: activity?.deal_id || null,
  user_id: activity?.user_id || null,
  description: activity?.description || activity?.notes || activity?.content || ''
});

const getProjectRow = (projectData = {}, tenantId) => ({
  tenant_id: tenantId,
  client_company_id: isUuid(projectData.client_company_id)
    ? projectData.client_company_id
    : null,
  is_internal: projectData.is_internal === true,
  name: projectData.name?.trim() || '',
  description: projectData.description ?? '',
  project_type: projectData.project_type || 'Salg',
  project_for: projectData.project_for || 'Internt prosjekt',
  status: projectData.status || 'Planlagt',
  start_date: projectData.start_date || null,
  end_date: projectData.end_date || null,
  budget: Number(projectData.budget) || 0,
  target_value: Number(projectData.target_value) || 0,
  target_unit: projectData.target_unit || 'kroner',
  target_period: projectData.target_period || 'Totalt for prosjektet',
  pricing_model: projectData.pricing_model || 'Ikke avklart',
  agreed_price: projectData.agreed_price === null || projectData.agreed_price === undefined
    ? null
    : Number(projectData.agreed_price) || 0,
  created_by: isUuid(projectData.created_by) ? projectData.created_by : null
});

const getProjectUpdateRow = (updates = {}, existingProject = {}) => {
  const updateRow = {
    updated_at: new Date().toISOString()
  };

  if (Object.prototype.hasOwnProperty.call(updates, 'client_company_id')) {
    updateRow.client_company_id = updates.client_company_id === null
      ? null
      : (isUuid(updates.client_company_id) ? updates.client_company_id : null);
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'is_internal')) {
    updateRow.is_internal = updates.is_internal === true;
  }

  const fields = [
    'name',
    'description',
    'project_type',
    'project_for',
    'status',
    'start_date',
    'end_date',
    'target_unit',
    'target_period',
    'pricing_model'
  ];

  fields.forEach(field => {
    if (Object.prototype.hasOwnProperty.call(updates, field)) {
      updateRow[field] = field === 'name'
        ? updates[field]?.trim() || ''
        : updates[field] ?? '';
    }
  });

  ['budget', 'target_value', 'agreed_price'].forEach(field => {
    if (Object.prototype.hasOwnProperty.call(updates, field)) {
      updateRow[field] = updates[field] === null
        ? null
        : Number(updates[field]) || 0;
    }
  });

  if (!Object.keys(updateRow).some(field => field !== 'updated_at')) {
    updateRow.name = existingProject.name || '';
  }

  return updateRow;
};

const getCompanyRow = (companyData = {}, tenantId) => {
  const inputId = getEntityId(companyData);
  const row = {
    ...(isUuid(inputId) ? { id: inputId } : {}),
    name: companyData.name?.trim() || 'Ukjent selskap',
    industry: companyData.industry || '',
    website: companyData.website || '',
    city: companyData.city || '',
    status: companyData.status || 'Lead',
    user_id: companyData.user_id || null,
    tenant_id: tenantId
  };

  const orgNumber = companyData.org_number
    || companyData.orgnr
    || companyData.organization_number
    || companyData.organizationNumber
    || companyData.organisasjonsnummer
    || companyData.orgNumber;

  if (orgNumber !== undefined) {
    row.org_number = normalizeOrgNumber(orgNumber);
  }

  const brregFields = getBrregFields(companyData);
  delete brregFields.org_number;

  return Object.fromEntries(
    Object.entries({
      ...row,
      ...brregFields
    }).filter(([, value]) => value !== undefined)
  );
};

export const useCRMData = () => {
  const context = useContext(CRMDataContext);

  if (!context) {
    throw new Error('useCRMData must be used within a CRMDataProvider');
  }

  return context;
};

export const CRMDataProvider = ({ children }) => {
  const {
    activeTenantId,
    activeTenant,
    loading: tenantLoading
  } = useTenant();
  const [companies, setCompanies] = React.useState([]);
  const [contacts, setContacts] = React.useState([]);
  const [deals, setDeals] = React.useState([]);
  const [activities, setActivities] = React.useState([]);
  const [budgets, setBudgets] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [projectsLoading, setProjectsLoading] = React.useState(false);
  const [projectsError, setProjectsError] = React.useState(null);
  const [pipelineStages, setPipelineStages] = React.useState([]);
  const [clientPipelines, setClientPipelines] = React.useState([]);
  const [pipelinesLoading, setPipelinesLoading] = React.useState(false);
  const [pipelinesError, setPipelinesError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  void activeTenant;

  if (!import.meta.env.PROD) {
    console.debug('[CRMDataProvider] tenant state', {
      activeTenantId,
      activeTenant,
      tenantsReady: Boolean(activeTenantId),
      tenantLoading
    });
  }

  const reloadProjects = async () => {
    setProjectsLoading(true);
    setProjectsError(null);

    if (!activeTenantId) {
      setProjects([]);
      setProjectsLoading(false);
      return { success: true, data: [] };
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('crm_projects_1788710000000')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .order('created_at', { ascending: false })
        .order('name', { ascending: true });

      if (fetchError) {
        console.error('[CRMDataProvider.reloadProjects] Supabase fetch-feil.', fetchError);
        setProjectsError(fetchError.message || 'Kunne ikke laste prosjekter.');
        setProjects([]);
        return { success: false, error: fetchError };
      }

      const normalizedProjects = normalizeProjectRows(data);
      setProjects(normalizedProjects);
      return { success: true, data: normalizedProjects };
    } catch (fetchError) {
      console.error('[CRMDataProvider.reloadProjects] Uventet fetch-feil.', fetchError);
      setProjectsError(fetchError.message || 'Kunne ikke laste prosjekter.');
      setProjects([]);
      return createError(fetchError.message || 'Kunne ikke laste prosjekter.');
    } finally {
      setProjectsLoading(false);
    }
  };

  const reloadClientPipelines = async () => {
    setPipelinesLoading(true);
    setPipelinesError(null);

    if (!activeTenantId) {
      setClientPipelines([]);
      setPipelinesLoading(false);
      return { success: true, data: [] };
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('crm_pipelines_1788709000000')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .order('created_at', { ascending: true })
        .order('name', { ascending: true });

      if (fetchError) {
        console.error('[CRMDataProvider.reloadClientPipelines] Supabase fetch-feil.', fetchError);
        setPipelinesError(fetchError.message || 'Kunne ikke laste oppdragsgiver-pipelines.');
        setClientPipelines([]);
        return { success: false, error: fetchError };
      }

      const normalizedPipelines = normalizePipelineRows(data);
      setClientPipelines(normalizedPipelines);
      return { success: true, data: normalizedPipelines };
    } catch (fetchError) {
      console.error('[CRMDataProvider.reloadClientPipelines] Uventet fetch-feil.', fetchError);
      setPipelinesError(fetchError.message || 'Kunne ikke laste oppdragsgiver-pipelines.');
      setClientPipelines([]);
      return createError(fetchError.message || 'Kunne ikke laste oppdragsgiver-pipelines.');
    } finally {
      setPipelinesLoading(false);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    setError(null);

    if (!activeTenantId) {
      setCompanies([]);
      setContacts([]);
      setDeals([]);
      setActivities([]);
      setBudgets([]);
      setProjects([]);
      setPipelineStages([]);
      setClientPipelines([]);
      setProjectsError(null);
      setPipelinesError(null);
      setLoading(false);
      return { success: true };
    }

    try {
      const [comp, cont, dl, act, budg, stages] = await Promise.all([
        supabase.from('companies_1710925200000').select('*').eq('tenant_id', activeTenantId),
        supabase.from('contacts_1710925200000').select('*').eq('tenant_id', activeTenantId),
        supabase.from('deals_1710925200000').select('*').eq('tenant_id', activeTenantId),
        supabase.from('activities_1710925200000').select('*').eq('tenant_id', activeTenantId),
        supabase.from('budgets_1710925200000').select('*').eq('tenant_id', activeTenantId),
        supabase
          .from('pipeline_stages_1710925200000')
          .select('*')
          .order('sort_order', { ascending: true })
      ]);

      const responseError = [comp, cont, dl, act, budg, stages]
        .find(response => response?.error);

      if (responseError?.error) {
        throw responseError.error;
      }

      setCompanies(normalizeCompanyRows(comp.data));
      setContacts(normalizeContactRows(cont.data));
      setDeals(normalizeDealRows(dl.data));
      setActivities(normalizeRows(act.data, 'activity'));
      setBudgets(Array.isArray(budg.data) ? budg.data : []);
      setPipelineStages(normalizeRows(stages.data, 'stage'));

      await Promise.all([
        reloadProjects(),
        reloadClientPipelines()
      ]);

      return { success: true };
    } catch (loadError) {
      const message = loadError?.message || 'Kunne ikke laste CRM-data.';
      setError(message);
      console.error('[CRM DATA LOAD]', loadError);
      return createError(message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadAllData();
  }, [activeTenantId]);

  const addProject = async (projectData = {}) => {
    if (!activeTenantId) {
      const message = 'Kan ikke lagre prosjektet uten aktiv organisasjon.';
      console.error('[CRMDataProvider.addProject] Mangler activeTenantId.');
      return createError(message);
    }

    try {
      const projectRow = getProjectRow(projectData, activeTenantId);
      const { data, error: insertError } = await supabase
        .from('crm_projects_1788710000000')
        .insert(projectRow)
        .select()
        .single();

      if (insertError) {
        console.error('[CRMDataProvider.addProject] Supabase insert-feil.', insertError);
        return { success: false, error: insertError };
      }

      const savedProject = normalizeProject(data);
      setProjects(current => [savedProject, ...current]);
      return { success: true, data: savedProject };
    } catch (saveError) {
      console.error('[CRMDataProvider.addProject] Uventet lagringsfeil.', saveError);
      return createError(saveError.message || 'Kunne ikke lagre prosjektet.');
    }
  };

  const updateProject = async (projectId, updates = {}) => {
    if (!activeTenantId || !isUuid(projectId)) {
      const message = 'Mangler gyldig prosjekt-ID eller aktiv organisasjon.';
      console.error('[CRMDataProvider.updateProject] Ugyldig prosjekt-ID eller activeTenantId.');
      return createError(message);
    }

    const existingProject = projects.find(project => project.id === projectId);

    if (!existingProject) {
      const message = 'Fant ikke prosjektet som skulle oppdateres.';
      console.error('[CRMDataProvider.updateProject] Fant ikke prosjektet.', projectId);
      return createError(message);
    }

    try {
      const updateRow = getProjectUpdateRow(updates, existingProject);
      const { data, error: updateError } = await supabase
        .from('crm_projects_1788710000000')
        .update(updateRow)
        .eq('id', projectId)
        .eq('tenant_id', activeTenantId)
        .select()
        .single();

      if (updateError) {
        console.error('[CRMDataProvider.updateProject] Supabase update-feil.', updateError);
        return { success: false, error: updateError };
      }

      const savedProject = normalizeProject(data);
      setProjects(current => current.map(project =>
        project.id === projectId ? savedProject : project
      ));

      return { success: true, data: savedProject };
    } catch (updateError) {
      console.error('[CRMDataProvider.updateProject] Uventet oppdateringsfeil.', updateError);
      return createError(updateError.message || 'Kunne ikke oppdatere prosjektet.');
    }
  };

  const addCompany = async (companyData = {}) => {
    if (!activeTenantId) {
      const error = new Error('Kan ikke lagre selskapet uten aktiv organisasjon.');
      console.error('[CRMDataProvider.addCompany] Mangler activeTenantId før insert.', {
        activeTenantId,
        tenantLoading
      });
      return createError(error.message);
    }

    if (!import.meta.env.PROD) {
      console.debug('[CRMDataProvider.addCompany] activeTenantId før insert', activeTenantId);
    }

    try {
      const companyRow = getCompanyRow(companyData, activeTenantId);
      const { data, error: insertError } = await supabase
        .from('companies_1710925200000')
        .insert(companyRow)
        .select()
        .single();

      if (insertError) {
        console.error('[CRMDataProvider.addCompany] Supabase insert-feil.', insertError);
        return { success: false, error: insertError };
      }

      const savedCompany = normalizeCompany({ ...companyData, ...data });
      setCompanies(current => [...current, savedCompany]);
      return { success: true, data: savedCompany };
    } catch (saveError) {
      console.error('[CRMDataProvider.addCompany] Uventet lagringsfeil.', saveError);
      return createError(saveError.message || 'Kunne ikke lagre selskapet.');
    }
  };

  const updateCompany = async (companyId, changes = {}) => {
    if (!isUuid(activeTenantId)) {
      return createError(invalidTenantMessage);
    }

    const existingCompany = companies.find(company =>
      company.company_id === companyId || company.id === companyId
    );

    if (!existingCompany) {
      return createError('Fant ikke selskapet som skulle oppdateres.');
    }

    try {
      const updateRow = {
        name: changes.name?.trim() || existingCompany.name,
        industry: changes.industry ?? existingCompany.industry ?? '',
        website: changes.website ?? existingCompany.website ?? '',
        city: changes.city ?? existingCompany.city ?? '',
        status: changes.status || existingCompany.status || 'Lead'
      };

      const orgNumber = changes.org_number
        ?? changes.orgnr
        ?? changes.organization_number
        ?? changes.organizationNumber
        ?? changes.organisasjonsnummer
        ?? changes.orgNumber
        ?? existingCompany.org_number;

      if (orgNumber !== undefined) {
        updateRow.org_number = normalizeOrgNumber(orgNumber);
      }

      const brregFields = getBrregFields({
        ...existingCompany,
        ...changes
      });
      delete brregFields.org_number;

      const { data, error: updateError } = await supabase
        .from('companies_1710925200000')
        .update({
          ...updateRow,
          ...brregFields
        })
        .eq('id', companyId)
        .eq('tenant_id', activeTenantId)
        .select()
        .single();

      if (updateError) {
        console.error('[CRMDataProvider.updateCompany] Supabase update-feil.', updateError);
        return { success: false, error: updateError };
      }

      const savedCompany = normalizeCompany({
        ...existingCompany,
        ...changes,
        ...data
      });

      setCompanies(current => current.map(company =>
        company.company_id === companyId || company.id === companyId
          ? savedCompany
          : company
      ));

      return { success: true, data: savedCompany };
    } catch (updateError) {
      console.error('[CRMDataProvider.updateCompany] Uventet oppdateringsfeil.', updateError);
      return createError(updateError.message || 'Kunne ikke oppdatere selskapet.');
    }
  };

  const addClientPipeline = async (pipelineData = {}) => {
    if (!activeTenantId) {
      const message = 'Kan ikke lagre pipelinen uten aktiv organisasjon.';
      console.error('[CRMDataProvider.addClientPipeline] Mangler activeTenantId.');
      return createError(message);
    }

    const pipelineProjectId = pipelineData.project_id || pipelineData.projectId || null;
    const ownerCompanyId = pipelineData.owner_company_id || pipelineData.ownerCompanyId || null;
    const validProjectId = isUuid(pipelineProjectId) ? pipelineProjectId : null;
    const validOwnerCompanyId = isUuid(ownerCompanyId) ? ownerCompanyId : null;

    if (!validProjectId && !validOwnerCompanyId) {
      const message = 'Pipelinen må ha en gyldig oppdragsgiver eller være knyttet til et prosjekt.';
      console.error('[CRMDataProvider.addClientPipeline] Mangler gyldig owner_company_id eller project_id.');
      return createError(message);
    }

    try {
      const pipelineRow = {
        tenant_id: activeTenantId,
        owner_company_id: validOwnerCompanyId || null,
        project_id: validProjectId || null,
        name: pipelineData.name?.trim() || '',
        description: pipelineData.description ?? '',
        status: pipelineData.status || 'Active',
        created_by: isUuid(pipelineData.created_by) ? pipelineData.created_by : null
      };

      const { data, error: insertError } = await supabase
        .from('crm_pipelines_1788709000000')
        .insert(pipelineRow)
        .select()
        .single();

      if (insertError) {
        console.error('[CRMDataProvider.addClientPipeline] Supabase insert-feil.', insertError);
        return { success: false, error: insertError };
      }

      const savedPipeline = normalizePipeline(data);
      setClientPipelines(current => [...current, savedPipeline]);
      return { success: true, data: savedPipeline };
    } catch (saveError) {
      console.error('[CRMDataProvider.addClientPipeline] Uventet lagringsfeil.', saveError);
      return createError(saveError.message || 'Kunne ikke lagre oppdragsgiver-pipeline.');
    }
  };

  const updateClientPipeline = async (pipelineId, updates = {}) => {
    if (!activeTenantId || !isUuid(pipelineId)) {
      return createError('Mangler gyldig pipeline-ID eller aktiv organisasjon.');
    }

    try {
      const updateRow = {
        name: updates.name?.trim() || '',
        description: updates.description ?? '',
        status: updates.status || 'Active',
        updated_at: new Date().toISOString()
      };

      const projectIdProvided =
        Object.prototype.hasOwnProperty.call(updates, 'project_id') ||
        Object.prototype.hasOwnProperty.call(updates, 'projectId');

      if (projectIdProvided) {
        const projectId = updates.project_id ?? updates.projectId;
        updateRow.project_id = projectId === null || projectId === undefined
          ? null
          : (isUuid(projectId) ? projectId : null);
      }

      const { data, error: updateError } = await supabase
        .from('crm_pipelines_1788709000000')
        .update(updateRow)
        .eq('id', pipelineId)
        .eq('tenant_id', activeTenantId)
        .select()
        .single();

      if (updateError) {
        console.error('[CRMDataProvider.updateClientPipeline] Supabase update-feil.', updateError);
        return { success: false, error: updateError };
      }

      const savedPipeline = normalizePipeline(data);
      setClientPipelines(current => current.map(pipeline =>
        pipeline.id === pipelineId ? savedPipeline : pipeline
      ));

      return { success: true, data: savedPipeline };
    } catch (updateError) {
      console.error('[CRMDataProvider.updateClientPipeline] Uventet oppdateringsfeil.', updateError);
      return createError(updateError.message || 'Kunne ikke oppdatere oppdragsgiver-pipeline.');
    }
  };

  const addContact = async (contactData = {}) => {
    if (!activeTenantId) {
      return createError('Kan ikke lagre kontakten uten aktiv organisasjon.');
    }

    try {
      const inputId = getEntityId(contactData);
      const contactRow = {
        ...(isUuid(inputId) ? { id: inputId } : {}),
        company_id: isUuid(contactData.company_id) ? contactData.company_id : null,
        name: contactData.name?.trim() || 'Ukjent kontakt',
        email: contactData.email?.trim() || contactData.epost || '',
        phone: contactData.phone || contactData.mobile || contactData.phone_number || '',
        role: contactData.title || contactData.role || '',
        role_in_buying: contactData.role_in_buying || 'User',
        linkedin_url: contactData.linkedin_url || '',
        score: Number(contactData.score) || 0,
        user_id: contactData.user_id || null,
        tenant_id: activeTenantId
      };

      const { data, error: insertError } = await supabase
        .from('contacts_1710925200000')
        .insert(contactRow)
        .select()
        .single();

      if (insertError) return { success: false, error: insertError };

      const relationStrength = data.relation_strength
        ?? data.relationStrength
        ?? data.relationship_strength
        ?? contactData.relation_strength
        ?? contactData.relationStrength
        ?? contactData.relationship_strength
        ?? 5;

      const savedContact = {
        ...contactData,
        ...data,
        title: data.title || data.job_title || data.position || data.role || contactData.title || contactData.role || '',
        phone: data.phone || data.mobile || data.phone_number || contactData.phone || contactData.mobile || '',
        mobile: data.mobile || data.phone || data.phone_number || contactData.mobile || contactData.phone || '',
        email: data.email || data.epost || contactData.email || contactData.epost || '',
        company_id: data.company_id || data.companyId || contactData.company_id || contactData.companyId || null,
        companyId: data.company_id || data.companyId || contactData.company_id || contactData.companyId || null,
        relation_strength: relationStrength,
        relationStrength,
        contact_id: data.id
      };

      setContacts(current => [...current, savedContact]);
      return { success: true, data: savedContact };
    } catch (saveError) {
      return createError(saveError.message || 'Kunne ikke lagre kontakten.');
    }
  };

  const updateContact = async (contactId, changes = {}) => {
    if (!isUuid(activeTenantId)) {
      return createError(invalidTenantMessage);
    }

    const existingContact = contacts.find(contact =>
      contact.contact_id === contactId || contact.id === contactId
    );

    if (!existingContact) {
      return createError('Fant ikke kontakten som skulle oppdateres.');
    }

    try {
      const companyId = changes.company_id ?? changes.companyId ?? existingContact.company_id;
      const updateRow = {
        company_id: isUuid(companyId) ? companyId : null,
        name: changes.name?.trim() || existingContact.name,
        email: changes.email?.trim() || changes.epost || existingContact.email || '',
        phone: changes.phone ?? changes.mobile ?? changes.phone_number ?? existingContact.phone ?? existingContact.mobile ?? '',
        role: changes.title ?? changes.job_title ?? changes.position ?? changes.role ?? existingContact.title ?? existingContact.role ?? '',
        role_in_buying: changes.role_in_buying || existingContact.role_in_buying || 'User',
        linkedin_url: changes.linkedin_url ?? existingContact.linkedin_url ?? '',
        score: Number(changes.score ?? existingContact.score) || 0
      };

      const { data, error: updateError } = await supabase
        .from('contacts_1710925200000')
        .update(updateRow)
        .eq('id', contactId)
        .eq('tenant_id', activeTenantId)
        .select()
        .single();

      if (updateError) return { success: false, error: updateError };

      const relationStrength = data.relation_strength
        ?? data.relationStrength
        ?? data.relationship_strength
        ?? changes.relation_strength
        ?? changes.relationStrength
        ?? changes.relationship_strength
        ?? existingContact.relation_strength
        ?? existingContact.relationStrength
        ?? existingContact.relationship_strength
        ?? 5;

      const savedContact = {
        ...existingContact,
        ...changes,
        ...data,
        title: data.title || data.job_title || data.position || data.role || changes.title || changes.job_title || changes.position || changes.role || existingContact.title || existingContact.role || '',
        phone: data.phone || data.mobile || data.phone_number || changes.phone || changes.mobile || changes.phone_number || existingContact.phone || existingContact.mobile || '',
        mobile: data.mobile || data.phone || data.phone_number || changes.mobile || changes.phone || changes.phone_number || existingContact.mobile || existingContact.phone || '',
        email: data.email || data.epost || changes.email || changes.epost || existingContact.email || existingContact.epost || '',
        company_id: data.company_id || data.companyId || companyId || null,
        companyId: data.company_id || data.companyId || companyId || null,
        relation_strength: relationStrength,
        relationStrength,
        contact_id: data.id
      };

      setContacts(current => current.map(contact =>
        contact.contact_id === contactId || contact.id === contactId
          ? savedContact
          : contact
      ));

      return { success: true, data: savedContact };
    } catch (updateError) {
      return createError(updateError.message || 'Kunne ikke oppdatere kontakten.');
    }
  };

  const addDeal = async (dealData = {}) => {
    if (!activeTenantId) {
      return createError('Kan ikke lagre dealen uten aktiv organisasjon.');
    }

    try {
      const inputId = getEntityId(dealData);
      const stageId = dealData.stage_id || dealData.stageId || dealData.pipelineStageId;
      const normalizedOwnerUserId =
        dealData.owner_user_id ||
        dealData.user_id ||
        dealData.ownerUserId ||
        dealData.assigned_user_id ||
        null;
      const dealRow = {
        ...(isUuid(inputId) ? { id: inputId } : {}),
        title: dealData.title?.trim() || 'Ny Lead',
        value: Number(dealData.value) || 0,
        status: dealData.status || 'Åpen',
        probability: Number(dealData.probability) || 10,
        expected_close_date: dealData.expected_close_date || null,
        company_id: isUuid(dealData.company_id) ? dealData.company_id : null,
        client_company_id: isUuid(dealData.client_company_id)
          ? dealData.client_company_id
          : null,
        pipeline_id: isUuid(dealData.pipeline_id) ? dealData.pipeline_id : null,
        contact_id: isUuid(dealData.contact_id) ? dealData.contact_id : null,
        stage_id: isUuid(stageId) ? stageId : null,
        user_id: dealData.user_id || normalizedOwnerUserId,
        tenant_id: activeTenantId
      };

      const { data, error: insertError } = await supabase
        .from('deals_1710925200000')
        .insert(dealRow)
        .select()
        .single();

      if (insertError) return { success: false, error: insertError };

      const savedDeal = normalizeDeal({ ...dealData, ...data });
      setDeals(current => [...current, savedDeal]);
      return { success: true, data: savedDeal };
    } catch (saveError) {
      return createError(saveError.message || 'Kunne ikke lagre deal/lead.');
    }
  };

  const updateDeal = async (dealId, changes = {}) => {
    if (!isUuid(activeTenantId)) {
      return createError(invalidTenantMessage);
    }

    const existingDeal = deals.find(deal =>
      deal.deal_id === dealId || deal.id === dealId
    );

    if (!existingDeal) {
      return createError('Fant ikke dealen som skulle oppdateres.');
    }

    try {
      const stageId = changes.stage_id || changes.stageId || existingDeal.stage_id;
      const clientCompanyId = changes.client_company_id ?? existingDeal.client_company_id;
      const pipelineId = changes.pipeline_id ?? existingDeal.pipeline_id;
      const updateRow = {
        title: changes.title?.trim() || existingDeal.title,
        value: Number(changes.value ?? existingDeal.value) || 0,
        status: changes.status || existingDeal.status || 'Åpen',
        probability: Number(changes.probability ?? existingDeal.probability) || 10,
        expected_close_date: changes.expected_close_date ?? existingDeal.expected_close_date ?? null,
        company_id: isUuid(changes.company_id ?? existingDeal.company_id)
          ? (changes.company_id ?? existingDeal.company_id)
          : null,
        client_company_id: isUuid(clientCompanyId) ? clientCompanyId : null,
        pipeline_id: isUuid(pipelineId) ? pipelineId : null,
        contact_id: isUuid(changes.contact_id ?? existingDeal.contact_id)
          ? (changes.contact_id ?? existingDeal.contact_id)
          : null,
        stage_id: isUuid(stageId) ? stageId : null
      };

      const { data, error: updateError } = await supabase
        .from('deals_1710925200000')
        .update(updateRow)
        .eq('id', dealId)
        .eq('tenant_id', activeTenantId)
        .select()
        .single();

      if (updateError) return { success: false, error: updateError };

      const savedDeal = normalizeDeal({ ...existingDeal, ...changes, ...data });
      setDeals(current => current.map(deal =>
        deal.deal_id === dealId || deal.id === dealId ? savedDeal : deal
      ));

      return { success: true, data: savedDeal };
    } catch (updateError) {
      return createError(updateError.message || 'Kunne ikke oppdatere dealen.');
    }
  };

  const deleteDeal = async dealId => {
    try {
      const { error: deleteError } = await supabase
        .from('deals_1710925200000')
        .delete()
        .eq('id', dealId)
        .eq('tenant_id', activeTenantId);

      if (deleteError) return { success: false, error: deleteError };

      setDeals(current => current.filter(deal =>
        deal.deal_id !== dealId && deal.id !== dealId
      ));

      return { success: true };
    } catch (deleteError) {
      return createError(deleteError.message || 'Kunne ikke slette dealen.');
    }
  };

  const addActivity = async activity => {
    if (!activeTenantId) {
      return createError('Kan ikke lagre aktiviteten uten aktiv organisasjon.');
    }

    const activityRow = {
      ...getActivityRow(activity),
      tenant_id: activeTenantId
    };

    if (!activityRow.title) {
      return createError('Aktiviteten må ha en tittel.');
    }

    try {
      const { data, error: insertError } = await supabase
        .from('activities_1710925200000')
        .insert(activityRow)
        .select()
        .single();

      if (insertError) return { success: false, error: insertError };

      const savedActivity = { ...data, activity_id: data.id };
      setActivities(current => [...current, savedActivity]);
      return { success: true, data: savedActivity };
    } catch (saveError) {
      return createError(saveError.message || 'Kunne ikke lagre aktiviteten.');
    }
  };

  const updateActivity = async (activityId, changes = {}) => {
    if (!activityId) {
      return createError('Mangler aktivitets-ID.');
    }

    const activityRow = getActivityRow(changes);
    delete activityRow.user_id;

    if (!activityRow.title) {
      return createError('Aktiviteten må ha en tittel.');
    }

    try {
      const { data, error: updateError } = await supabase
        .from('activities_1710925200000')
        .update(activityRow)
        .eq('id', activityId)
        .select()
        .single();

      if (updateError) return { success: false, error: updateError };

      const updatedActivity = { ...data, activity_id: data.id };
      setActivities(current => current.map(activity =>
        activity.activity_id === activityId || activity.id === activityId
          ? updatedActivity
          : activity
      ));

      return { success: true, data: updatedActivity };
    } catch (updateError) {
      return createError(updateError.message || 'Kunne ikke oppdatere aktiviteten.');
    }
  };

  const runtimeValue = {
    companies,
    contacts,
    deals,
    activities,
    budgets,
    projects,
    projectsLoading,
    projectsError,
    pipelineStages,
    clientPipelines,
    pipelinesLoading,
    pipelinesError,
    loading,
    error,
    ready: !tenantLoading && Boolean(activeTenantId),
    activeTenantId,
    loadAllData,
    reloadProjects,
    reloadClientPipelines,
    addProject,
    updateProject,
    addCompany,
    updateCompany,
    addContact,
    updateContact,
    addDeal,
    updateDeal,
    deleteDeal,
    addClientPipeline,
    updateClientPipeline,
    addActivity,
    updateActivity
  };

  const value = {
    companies: [],
    contacts: [],
    deals: [],
    activities: [],
    budgets: [],
    projects: [],
    projectsLoading: false,
    projectsError: null,
    pipelineStages: [],
    clientPipelines: [],
    pipelinesLoading: false,
    pipelinesError: null,
    loading: false,
    error: null,
    ready: false,
    activeTenantId: null,
    loadAllData: notConnectedResult,
    reloadProjects: notConnectedResult,
    reloadClientPipelines: notConnectedResult,
    addProject: notConnectedResult,
    updateProject: notConnectedResult,
    addCompany: notConnectedResult,
    updateCompany: notConnectedResult,
    addContact: notConnectedResult,
    updateContact: notConnectedResult,
    addDeal: notConnectedResult,
    updateDeal: notConnectedResult,
    deleteDeal: notConnectedResult,
    addClientPipeline: notConnectedResult,
    updateClientPipeline: notConnectedResult,
    addActivity: notConnectedResult,
    updateActivity: notConnectedResult,
    ...runtimeValue
  };

  return (
    <CRMDataContext.Provider value={value}>
      {children}
    </CRMDataContext.Provider>
  );
};